import React from 'react'
import { Popover, Table, Tooltip } from 'antd'
import { MultiUom } from 'Store/product/types'
import { InfoCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { filterUoms, numberWithComma } from 'helpers/format'
import { cloneDeep } from 'lodash'
import { DefaultStatus } from 'helpers/constanst'

type Props = {
  uoms: MultiUom[]
  showPurchasePrice?: boolean
  showQuantity?: boolean
  showMaster?: boolean
  showBarcode?: boolean
  label?: any
  priceKey?: string
  priceTitle?: string
  quantityType?: 'offline' | 'online' | 'all'
}

const ProductMultiUoms: React.FunctionComponent<Props> = props => {
  const {
    uoms,
    showPurchasePrice = false,
    showQuantity = true,
    showMaster = true,
    showBarcode = false,
    label,
    priceKey = '',
    priceTitle = 'ราคา',
    quantityType = 'all',
  } = props
  const hasMultiUom = uoms?.length > 1
  const masterUom = uoms?.find(item => item.is_master)

  const renderTooltip = (label: any, title: any) => {
    return (
      <Popover content={title} trigger="hover" placement="bottom">
        {label} <InfoCircleOutlined style={{ cursor: 'pointer' }} />
      </Popover>
    )
  }

  let columns = [
    {
      title: showMaster ? <Description>หน่วยสินค้า</Description> : <Description>หน่วยสินค้าอื่น</Description>,
      dataIndex: 'uom_name',
      key: 'uom_name',
      render: (val: string, record: MultiUom) => (
        <DescriptionBold>{`${val} ${
          record?.is_master ? '(หน่วยสินค้าหลัก)' : `(${record.multiplier} ${masterUom?.uom_name || ''})`
        }`}</DescriptionBold>
      ),
    },
  ] as any[]

  if (showBarcode) {
    columns.push({
      title: <Description>{`บาร์โค้ด`}</Description>,
      dataIndex: 'barcode',
      key: 'barcode',
      render: (val: number, record: MultiUom) => <Description> {val} </Description>,
    })
  }

  if (priceKey) {
    columns.push({
      title: <Description>{`${priceTitle}/หน่วย`}</Description>,
      dataIndex: priceKey,
      key: priceKey,
      render: (price: number, record: MultiUom) => (
        <Description> {numberWithComma(price, true)} บาท</Description>
      ),
    })
  }

  if (showPurchasePrice) {
    const title = (
      <Tooltip
        placement="topLeft"
        title={
          'ราคาซื้อ ไม่ใช่ต้นทุนที่แท้จริงของสินค้า เป็นเพียงข้อมูลสินค้าที่ร้านค้าบันทึกไว้เท่านั้น ร้านค้าสามารถแก้ไขราคาซื้อได้ที่หน้า แก้ไขตัวเลือกสินค้า'
        }
      >
        <Description>
          ราคาซื้อ <InfoCircleOutlined style={{ cursor: 'pointer' }} />
        </Description>
      </Tooltip>
    )
    columns.push({
      title: title,
      dataIndex: 'purchase_price',
      key: 'purchase_price',
      render: (price: number, record: MultiUom) => (
        <Description> {numberWithComma(price, true)} บาท</Description>
      ),
    })
  }

  if (showQuantity) {
    columns.push({
      title: <Description>สต๊อก</Description>,
      dataIndex: 'uom_name',
      key: 'uom_name',
      render: (val: string, record: MultiUom) => {
        const isMaster = record?.is_master || false
        let quantity = (record?.quantity || 0) + (record?.quantity_offline || 0)
        if (quantityType === 'offline') {
          quantity = record?.quantity_offline || 0
        }
        if (quantityType === 'online') {
          quantity = record?.quantity || 0
        }
        return (
          <Description>
            {isMaster ? <span>&nbsp;&nbsp;</span> : '≈'} {numberWithComma(quantity)} {record?.uom_name}
          </Description>
        )
      },
    })
  }

  let uomsData = cloneDeep(filterUoms(uoms, true))
  if (!showMaster) {
    uomsData = uomsData?.filter(item => !item.is_master)
  }

  const uomTitle = uoms.map(item => item?.uom_name || '')

  //const title = label ? label : uomTitle.join(',')
  const title = label ? label : masterUom?.uom_name || ''

  return (
    <div>
      {hasMultiUom
        ? renderTooltip(
            title,
            <Table
              columns={columns}
              dataSource={uomsData}
              pagination={false}
              size="small"
              bordered={false}
              scroll={{ x: '400px' }}
            />,
          )
        : title}
    </div>
  )
}

const Description = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const DescriptionBold = styled(Description)`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
`

export default ProductMultiUoms
